import { useCallback, useEffect, useMemo, useState } from 'react'

import { useBranch } from '@/contexts/BranchContext'
import { fetchServiceCategories } from '@/lib/api/serviceCategories'
import { fetchServices } from '@/lib/api/services'

import {
  filterActiveServices,
  filterCategoriesForPos,
} from './posCatalog'
import { isBrowserOffline, loadPosCatalogCache, savePosCatalogCache } from './posCatalogOfflineCache'

/**
 * @param {object} options
 * @param {import('./posCatalog').PosCatalogGenderFilter} options.catalogGenderFilter
 * @param {number | null} options.activeCategoryId
 * @param {(categoryId: number) => void} options.onCategoryChange
 */
export function usePosCatalog({ catalogGenderFilter, activeCategoryId, onCategoryChange }) {
  const { activeBranchId } = useBranch()
  const [categories, setCategories] = useState([])
  const [services, setServices] = useState([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [loadingServices, setLoadingServices] = useState(false)
  const [error, setError] = useState('')

  const visibleCategories = useMemo(
    () => filterCategoriesForPos(categories, catalogGenderFilter),
    [categories, catalogGenderFilter],
  )

  const visibleServices = useMemo(() => filterActiveServices(services), [services])

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true)
    setError('')
    try {
      const items = await fetchServiceCategories()
      setCategories(items)
      savePosCatalogCache(activeBranchId, 'categories', items)
      return items
    } catch (err) {
      const cached = loadPosCatalogCache(activeBranchId, 'categories')
      if (cached) {
        setCategories(cached)
        setError(isBrowserOffline() ? 'Offline — showing cached service categories.' : '')
        return cached
      }
      setCategories([])
      setError(err?.response?.data?.message ?? 'Unable to load service categories.')
      return []
    } finally {
      setLoadingCategories(false)
    }
  }, [activeBranchId])

  const loadServices = useCallback(async (categoryId) => {
    if (!categoryId) {
      setServices([])
      return []
    }

    setLoadingServices(true)
    setError('')
    try {
      const items = await fetchServices({ categoryId })
      setServices(items)
      savePosCatalogCache(activeBranchId, 'services', items, categoryId)
      return items
    } catch (err) {
      const cached = loadPosCatalogCache(activeBranchId, 'services', categoryId)
      if (cached) {
        setServices(cached)
        setError(isBrowserOffline() ? 'Offline — showing cached services for this category.' : '')
        return cached
      }
      setServices([])
      setError(err?.response?.data?.message ?? 'Unable to load services.')
      return []
    } finally {
      setLoadingServices(false)
    }
  }, [activeBranchId])

  useEffect(() => {
    if (!activeBranchId) {
      setCategories([])
      setServices([])
      setLoadingCategories(false)
      return
    }

    let active = true

    async function init() {
      await loadCategories()
      if (!active) return
    }

    init()

    return () => {
      active = false
    }
  }, [activeBranchId, loadCategories])

  useEffect(() => {
    if (visibleCategories.length === 0) {
      return
    }

    const categoryStillVisible = visibleCategories.some(
      (category) => category.id === activeCategoryId,
    )

    if (!categoryStillVisible) {
      onCategoryChange(visibleCategories[0].id)
    }
  }, [visibleCategories, activeCategoryId, onCategoryChange])

  useEffect(() => {
    if (!activeBranchId || !activeCategoryId) {
      setServices([])
      return
    }

    let active = true

    async function init() {
      await loadServices(activeCategoryId)
      if (!active) return
    }

    init()

    return () => {
      active = false
    }
  }, [activeBranchId, activeCategoryId, loadServices])

  const reload = useCallback(async () => {
    await loadCategories()
    if (activeCategoryId) {
      await loadServices(activeCategoryId)
    }
  }, [activeCategoryId, loadCategories, loadServices])

  return {
    categories: visibleCategories,
    services: visibleServices,
    loadingCategories,
    loadingServices,
    error,
    reload,
  }
}
